import { useId, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { useTranslation } from 'utils/translation';

interface QRCodeProps {
  value: string;
  bgColor?: string;
  fgColor?: string;
  level?: 'L' | 'M' | 'Q' | 'H';
  className?: string;
  onClick?(): void;
  showLabel?: boolean;
  customLabel?: string;
  paddingX?: number;
  paddingY?: number;
}

const QR_SIZE = 100;
const LABEL_SPACING = 8;
const LABEL_FONT_SIZE = 14;
const LABEL_HEIGHT = LABEL_SPACING + LABEL_FONT_SIZE;

export default function QRCode({
  value,
  bgColor = '#ffffff',
  fgColor = '#000000',
  level = 'L',
  className,
  onClick,
  showLabel = true,
  customLabel = '',
  paddingX = 0,
  paddingY = 0,
}: QRCodeProps) {
  const id = useId();
  const { t } = useTranslation();
  const containerRef = useRef<HTMLDivElement>(null);
  const contentHeight = showLabel ? QR_SIZE + LABEL_HEIGHT : QR_SIZE;
  const totalWidth = QR_SIZE + paddingX * 2;
  const totalHeight = contentHeight + paddingY * 2;
  const textLabel = customLabel ?? value;

  const downloadSvg = () => {
    const svg = containerRef.current?.querySelector('svg');
    if (!svg) return;

    const serializer = new XMLSerializer();
    const svgString = serializer.serializeToString(svg);
    const blob = new Blob([svgString], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `${textLabel}.svg`;
    link.click();

    URL.revokeObjectURL(url);
  };

  return (
    <div
      className="cursor-pointer"
      ref={containerRef}
      title={t('Click to download')}
      onClick={downloadSvg}
    >
      <svg
        id={id}
        width={totalWidth}
        height={totalHeight}
        viewBox={`0 0 ${totalWidth} ${totalHeight}`}
        xmlns="http://www.w3.org/2000/svg"
        className={className}
      >
        <rect width={totalWidth} height={totalHeight} fill={bgColor} />
        <g transform={`translate(${paddingX}, ${paddingY})`}>
          <QRCodeSVG
            onClick={onClick}
            value={value}
            size={QR_SIZE}
            bgColor={bgColor}
            fgColor={fgColor}
            level={level}
          />
        </g>
        {showLabel && (
          <text
            x={totalWidth / 2}
            y={paddingY + QR_SIZE + LABEL_HEIGHT}
            textAnchor="middle"
            fontFamily="Arial, sans-serif"
            fontSize={LABEL_FONT_SIZE}
            fontWeight="bold"
            fill={fgColor}
          >
            {textLabel}
          </text>
        )}
      </svg>
    </div>
  );
}
